/**
 * Thin wrappers over the topbar pills (game name, round id, countdown,
 * status, balance, bet total) and the floating result toast / result ball.
 */
import { COUNTDOWN_DANGER_THRESHOLD_S } from '../config';

/** Counter-up animation duration for balance / bet pill changes (ms). */
const COUNTER_TWEEN_MS = 400;

export class Topbar {
  private readonly gameLabel: HTMLElement;
  private readonly roundLabel: HTMLElement;
  private readonly statusLabel: HTMLElement;
  private readonly balanceLabel: HTMLElement;
  private readonly betLabel: HTMLElement;
  private readonly countdownPill: HTMLElement;
  private readonly toast: HTMLElement;
  private readonly resultDisplay: HTMLElement;
  private readonly splash: HTMLElement;
  private readonly splashFill: HTMLElement;
  private readonly splashSub: HTMLElement;

  /** Last committed balance value — drives count-up tween start point. */
  private balanceValue = 0;
  private betTotalValue = 0;
  /** Monotonic tween IDs — each new tween bumps these so any still-running
   *  rAF chain from a prior tween can detect it's been superseded and bail. */
  private balanceTweenId = 0;
  private betTweenId = 0;

  constructor() {
    this.gameLabel = req('game-label');
    this.roundLabel = req('round-label');
    this.statusLabel = req('status-pill');
    this.balanceLabel = req('balance-label');
    this.betLabel = req('bet-label');
    this.countdownPill = req('countdown-pill');
    this.toast = req('result-toast');
    this.resultDisplay = req('result-display');
    this.splash = req('splash');
    this.splashFill = req('splash-fill');
    this.splashSub = req('splash-sub');
  }

  setGame(name: string): void {
    this.gameLabel.textContent = name;
  }
  setRound(roundId: string): void {
    this.roundLabel.textContent = roundId ? roundId.slice(-6) : '—';
  }
  setStatus(text: string): void {
    this.statusLabel.textContent = text;
  }
  setBalance(n: number): void {
    const id = ++this.balanceTweenId;
    tweenNumber(this.balanceValue, n, COUNTER_TWEEN_MS, () => id === this.balanceTweenId, (v, done) => {
      this.balanceLabel.textContent = fmt(v);
      if (done) this.balanceValue = n;
    });
  }
  setBetTotal(n: number): void {
    const id = ++this.betTweenId;
    tweenNumber(this.betTotalValue, n, COUNTER_TWEEN_MS, () => id === this.betTweenId, (v, done) => {
      this.betLabel.textContent = fmt(v);
      if (done) this.betTotalValue = n;
    });
  }
  setCountdown(sec: number, mode: 'betting' | 'idle' = 'betting'): void {
    if (mode === 'idle') {
      this.countdownPill.textContent = '—';
      this.countdownPill.classList.remove('danger');
      return;
    }
    this.countdownPill.textContent = `${Math.max(0, sec).toString().padStart(2, '0')}s`;
    this.countdownPill.classList.toggle(
      'danger',
      sec <= COUNTDOWN_DANGER_THRESHOLD_S && sec > 0,
    );
  }

  /** Hide the countdown pill entirely — used in single-player where the
   *  player picks their own pace. The pill is re-enabled by setCountdown(). */
  hideCountdown(): void {
    this.countdownPill.style.display = 'none';
  }
  showCountdown(): void {
    this.countdownPill.style.display = '';
  }

  showToast(text: string, variant: 'win' | 'lose' | 'info'): void {
    this.toast.textContent = text;
    this.toast.classList.remove('win', 'lose');
    if (variant === 'win') this.toast.classList.add('win');
    if (variant === 'lose') this.toast.classList.add('lose');
    this.toast.classList.add('visible');
  }
  hideToast(): void {
    this.toast.classList.remove('visible');
  }

  /** Big floating ball over the canvas — used to make the winning number
   *  feel like a tangible drop. */
  showResultBall(num: number, colorClass: string): void {
    this.resultDisplay.innerHTML = '';
    const ball = document.createElement('div');
    ball.className = `result-ball ${colorClass}`;
    ball.textContent = String(num);
    this.resultDisplay.appendChild(ball);
    this.resultDisplay.classList.add('visible');
  }
  hideResultBall(): void {
    this.resultDisplay.classList.remove('visible');
  }

  setSplashProgress(ratio: number, sub?: string): void {
    const pct = Math.max(0, Math.min(1, ratio));
    this.splashFill.style.width = `${pct * 100}%`;
    if (sub) this.splashSub.textContent = sub;
  }
  dismissSplash(): void {
    this.splash.classList.add('hidden');
    window.setTimeout(() => {
      this.splash.style.display = 'none';
    }, 500);
  }
}

function req(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing #${id}`);
  return el;
}
function fmt(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

/**
 * Tween an integer counter from `from` to `to` over `durationMs` using
 * easeOutCubic. Calls `onFrame(value, done)` each animation frame.
 * `isCurrent` is checked before each frame — if a newer tween has started
 * (token mismatch), the chain stops without firing further updates.
 */
function tweenNumber(
  from: number,
  to: number,
  durationMs: number,
  isCurrent: () => boolean,
  onFrame: (value: number, done: boolean) => void,
): void {
  if (from === to || durationMs <= 0) {
    onFrame(to, true);
    return;
  }
  const start = performance.now();
  const delta = to - from;
  const step = (now: number): void => {
    if (!isCurrent()) return;
    const t = Math.min(1, (now - start) / durationMs);
    // easeOutCubic — fast start, gentle settle.
    const eased = 1 - Math.pow(1 - t, 3);
    if (t >= 1) {
      onFrame(to, true);
    } else {
      onFrame(from + delta * eased, false);
      requestAnimationFrame(step);
    }
  };
  requestAnimationFrame(step);
}
